import { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SendIcon from "@mui/icons-material/Send";
import { confirmAlert } from "react-confirm-alert";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { save, send } from "./api-evento";
import { decreaseQuantAprovacaoEvento, increaseQuantAprovacaoEvento } from "./actions/actions";
import { ADMIN, COORDENADOR } from "../../Usuario/Perfil";
import { getBase64 } from "../../../util/Base64";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { dateTimeFormat } from "../../../util/DateUtil";
import { errorToast, success } from "../../../util/ToastUtil";
import SunEditorUI from "../../UI/SunEditorUI";
import TextFieldDateTime from "../../UI/TextFieldDateTime";

const ENVIAR_POR = ["Aluno", "Turma"];
const ENVIAR_PARA = ["Ambos", "Alunos", "Responsáveis"];

const SEND_ERRORS = {
  "No turma registered": "O evento não possui turmas cadastradas para o envio.",
  "No aluno registered": "O evento não possui alunos cadastrados para o envio.",
};

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: "uppercase" }}>
    {children}
  </Typography>
);

const InfoItem = ({ label, value }) => (
  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2">{value || "--"}</Typography>
  </Grid>
);

export default function EventoForm({ evento, setEvento, onSaved, onDelete }) {
  const dispatch = useDispatch();
  const usuario = useSelector((state) => state.usuario);
  const [isPerforming, setIsPerforming] = useState(false);
  const fileInput = useRef(null);

  const podeAprovar = evento.codigo && (usuario?.perfil === ADMIN || usuario?.perfil === COORDENADOR);
  const updateField = (name, value) => setEvento((prevState) => ({ ...prevState, [name]: value }));
  const handleChange = (event) => updateField(event.target.name, event.target.value);

  const handleChangeImage = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    getBase64(file).then((result) => updateField("banner", result));
  };

  const validate = () => {
    if (!evento.enviarPor) {
      errorToast("Selecione por qual opção será enviado o evento.");
      return false;
    }
    if (!evento.enviarPara) {
      errorToast("Selecione para quem será enviado o evento.");
      return false;
    }
    if (!evento.dataHoraInicio || !evento.dataHoraFim) {
      errorToast("Informe a data e hora de início e fim.");
      return false;
    }
    if (dayjs(evento.dataHoraFim).isBefore(dayjs(evento.dataHoraInicio))) {
      errorToast("A data e hora de fim deve ser depois do início.");
      return false;
    }
    return true;
  };

  const clickSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;
    const eNovo = !evento.codigo;
    setIsPerforming(true);
    save(evento)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível salvar o evento.");
          return;
        }
        // Evento novo entra na contagem de pendências de aprovação.
        if (eNovo) dispatch(increaseQuantAprovacaoEvento());
        success("Salvo com sucesso.");
        onSaved(data, eNovo);
        setEvento(data);
      })
      .finally(() => setIsPerforming(false));
  };

  const handleAprove = () => {
    setIsPerforming(true);
    save({ ...evento, aprovado: !evento.aprovado })
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível alterar a aprovação.");
          return;
        }
        dispatch(data.aprovado ? decreaseQuantAprovacaoEvento() : increaseQuantAprovacaoEvento());
        success(data.aprovado ? "Evento aprovado com sucesso." : "Aprovação removida com sucesso.");
        onSaved(data, false);
        setEvento(data);
      })
      .finally(() => setIsPerforming(false));
  };

  const sendEvento = () => {
    setIsPerforming(true);
    send(evento.codigo)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(SEND_ERRORS[data?.message] || data?.message || "Não foi possível enviar o evento.");
          return;
        }
        success("Evento enviado com sucesso.");
        onSaved(data, false);
        setEvento(data);
      })
      .finally(() => setIsPerforming(false));
  };

  const handleSend = () =>
    confirmAlert({
      title: "Alerta",
      message: "Deseja enviar este evento? Após confirmada, esta operação não poderá ser desfeita.",
      buttons: [
        { label: "Sim", onClick: sendEvento },
        { label: "Não", onClick: () => {} },
      ],
    });

  return (
    <form onSubmit={clickSubmit}>
      <Stack spacing={3}>
        <Stack spacing={2}>
          <SectionTitle>Destinatários</SectionTitle>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                size="small"
                fullWidth
                label="Enviar por"
                name="enviarPor"
                disabled={evento.enviado}
                value={evento.enviarPor || ""}
                onChange={handleChange}
              >
                {ENVIAR_POR.map((ep) => (
                  <MenuItem key={ep} value={ep}>
                    {ep}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                size="small"
                fullWidth
                label="Enviar para"
                name="enviarPara"
                disabled={evento.enviado}
                value={evento.enviarPara || ""}
                onChange={handleChange}
              >
                {ENVIAR_PARA.map((ep) => (
                  <MenuItem key={ep} value={ep}>
                    {ep}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          {!evento.codigo && evento.enviarPor && (
            <Typography variant="caption" color="text.secondary">
              Salve o evento para adicionar {evento.enviarPor === "Turma" ? "as turmas" : "os alunos"}.
            </Typography>
          )}
        </Stack>

        <Divider />

        <Stack spacing={2}>
          <SectionTitle>Conteúdo</SectionTitle>
          <TextField
            size="small"
            required
            fullWidth
            label="Título"
            name="titulo"
            onChange={handleChange}
            value={evento.titulo || ""}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextFieldDateTime
                label="Início"
                value={evento.dataHoraInicio || null}
                onChange={(value) => updateField("dataHoraInicio", dayjs(value))}
                slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextFieldDateTime
                label="Fim"
                value={evento.dataHoraFim || null}
                onChange={(value) => updateField("dataHoraFim", dayjs(value))}
                slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
              />
            </Grid>
          </Grid>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            <Box
              sx={{
                width: 240,
                aspectRatio: "16 / 9",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "action.hover",
                border: 1,
                borderColor: "divider",
                color: "text.secondary",
              }}
            >
              {evento.banner ? (
                <Box
                  component="img"
                  src={evento.banner}
                  alt="Banner do evento"
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <Stack alignItems="center" spacing={0.5}>
                  <ImageOutlinedIcon />
                  <Typography variant="caption">Sem banner</Typography>
                </Stack>
              )}
            </Box>
            <input
              ref={fileInput}
              type="file"
              accept=".jpg,.jpeg,.png"
              hidden
              onChange={handleChangeImage}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => fileInput.current?.click()}>
                {evento.banner ? "Trocar Banner" : "Selecionar Banner"}
              </Button>
              {evento.banner && (
                <Button color="inherit" onClick={() => updateField("banner", null)}>
                  Remover Banner
                </Button>
              )}
            </Stack>
          </Stack>

          <SunEditorUI
            onChange={(value) => updateField("descricao", value.replace(/[​-‍﻿]/g, ""))}
            content={evento.descricao || ""}
          />
        </Stack>

        {evento.usuario && (
          <>
            <Divider />
            <Stack spacing={2}>
              <SectionTitle>Informações</SectionTitle>
              <Grid container spacing={2}>
                <InfoItem label="Criado por" value={`${evento.usuario.nome} - ${evento.usuario.perfil}`} />
                <InfoItem label="Criado em" value={evento.createdAt && dateTimeFormat(evento.createdAt)} />
                <InfoItem
                  label="Última atualização"
                  value={evento.lastModified && dateTimeFormat(evento.lastModified)}
                />
              </Grid>
            </Stack>
          </>
        )}

        <Divider />

        <Stack direction={{ xs: "column-reverse", sm: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            {evento.codigo && (
              <Button
                color="error"
                startIcon={<DeleteOutlineIcon />}
                disabled={isPerforming}
                onClick={() => onDelete(evento)}
              >
                Remover
              </Button>
            )}
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            {podeAprovar && (
              <Button
                variant="outlined"
                color="secondary"
                disabled={evento.enviado || isPerforming}
                onClick={handleAprove}
              >
                {evento.aprovado ? "Remover Aprovação" : "Aprovar"}
              </Button>
            )}
            <Button type="submit" variant="contained" startIcon={<SaveOutlinedIcon />} disabled={isPerforming}>
              Salvar
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<SendIcon />}
              disabled={!evento.codigo || evento.enviado || !evento.aprovado || isPerforming}
              onClick={handleSend}
            >
              Enviar
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </form>
  );
}
